import { useRef, useState } from 'react';
import { Apple, Beef, Check, Droplet, Wheat } from 'lucide-react';

import { DAY_EATEN, DAY_TARGET, PLATE_MEALS, SEARCHED_FOOD, SNACK, TODAY_INDEX, WEEK_DATES, WEEK_SHARES, formatNumber, useLanding } from './landingData';
import { Pill, Ring, SectionHead } from './parts';

const MACROS = [
  { key: 'protein', Icon: Beef },
  { key: 'carbs', Icon: Wheat },
  { key: 'fat', Icon: Droplet },
];
const DAY_SHORT_KEYS = ['monShort', 'tueShort', 'wedShort', 'thuShort', 'friShort', 'satShort', 'sunShort'];

const sum = (foods, key) => foods.reduce((total, food) => total + food[key], 0);

/**
 * Calorias do dia (Pro), drawn from the app's B · Anéis: the day in rings (calories, then protein, carbs and fat) and
 * the week in small rings; the next meal with "Comi o planejado", which works here (it logs the snack and the rings
 * move); and the add-food selection holding planned and searched foods together. No live data: a demo day.
 */
export const HomeCalories = () => {
  const { t, language, name } = useLanding();
  const c = (key) => t(`pages.Home.calories.${key}`);
  const ui = (key, options) => t(`pages.CalorieTracker.ui.${key}`, options);
  const fmt = (value) => formatNumber(Math.round(value), language);
  const [logged, setLogged] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const logRef = useRef(null);
  const undoRef = useRef(null);

  const snackKcal = sum(SNACK.foods, 'calories');
  const eaten = Object.fromEntries(Object.entries(DAY_EATEN).map(([key, value]) => [key, value + (logged ? sum(SNACK.foods, key) : 0)]));
  const share = eaten.calories / DAY_TARGET.calories;
  const meal = name(SNACK.name);
  // Two of the plan's foods and one searched, in one selection
  const picks = [
    { ...SNACK.foods[0], source: c('sourcePlan') },
    { ...SNACK.foods[1], source: c('sourcePlan') },
    { ...SEARCHED_FOOD, source: c('sourceSearch') },
  ];

  const toggle = (next) => {
    setLogged(next);
    setAnnouncement(next ? ui('toastPlanned', { meal }) : '');
    // The control just pressed gives way to the other one: the focus follows it
    requestAnimationFrame(() => (next ? undoRef : logRef).current?.focus({ preventScroll: true }));
  };

  return (
    <section id="calorias" className="lp-sec" aria-labelledby="lp-cal-title">
      <div className="lp-wrap">
        <SectionHead id="lp-cal-title" title={c('title')} sub={c('sub')} pill={<Pill kind="pro">{t('pages.Home.build.pro')}</Pill>} />
        <p className="lp-lead lp-lead--more">{c('rings')}</p>

        <div className="lp-cal" role="group" aria-label={c('demoLabel')}>
          <div className="lp-cal__main">
            <div className="lp-cal__day">
              <p className="lp-cal__when">
                {c('today')} <Pill kind="neutral">{t('pages.Home.demo.example')}</Pill>
              </p>
              <Ring share={share} size={212} stroke={18} className="lp-cal__big">
                <span className="lp-cal__in">
                  <b>{fmt(eaten.calories)}</b>
                  <span>{ui('ofTarget', { kcal: fmt(DAY_TARGET.calories) })}</span>
                </span>
              </Ring>
              <p className="lp-cal__left">
                {ui('left', { kcal: fmt(DAY_TARGET.calories - eaten.calories) })} <span>· {Math.round(share * 100)}%</span>
              </p>
              <ul className="lp-cal__macros">
                {MACROS.map(({ key, Icon }) => (
                  <li key={key}>
                    <Ring share={eaten[key] / DAY_TARGET[key]} size={62} stroke={6.5}>
                      <Icon className="lp-cal__icon" aria-hidden="true" />
                    </Ring>
                    <span className="lp-cal__mname">{t(`pages.DietPlan.${key}`)}</span>
                    <span className="lp-cal__grams">
                      {fmt(eaten[key])}
                      <small>{` / ${fmt(DAY_TARGET[key])} g`}</small>
                    </span>
                  </li>
                ))}
              </ul>
              <div className="lp-cal__week" aria-hidden="true">
                {WEEK_SHARES.map((weekShare, index) => (
                  <span key={DAY_SHORT_KEYS[index]} className={`lp-cal__wd${index === TODAY_INDEX ? ' is-today' : ''}${index > TODAY_INDEX ? ' is-later' : ''}`}>
                    <Ring share={index === TODAY_INDEX ? share : weekShare} size={38} stroke={4.5} className={weekShare >= 0.95 ? 'is-hit' : ''}>
                      <b>{WEEK_DATES[index]}</b>
                    </Ring>
                    {t(`pages.DietPlan.${DAY_SHORT_KEYS[index]}`)}
                  </span>
                ))}
              </div>
            </div>

            {/* The day's meals on a line (desktop): logging the snack checks it and Jantar comes up next */}
            <div className="lp-cal__tl" aria-hidden="true">
              <p className="lp-cal__tl-head">
                <b>{ui('yourDay')}</b>
                <span>{ui('dayOf', { kcal: fmt(eaten.calories), target: fmt(DAY_TARGET.calories) })}</span>
              </p>
              <ul>
                {PLATE_MEALS.map((item, index) => {
                  const isDone = item.state === 'done' || (logged && item.state === 'next');
                  const isNext = logged ? item.state === 'later' : item.state === 'next';
                  return (
                    <li key={item.en}>
                      <span className={`lp-cal__node${isDone ? ' is-done' : ''}${isNext ? ' is-next' : ''}`}>{isDone && <Check />}</span>
                      <span className="lp-cal__tl-text">
                        <b>{name(item)}</b>
                        <span>
                          {isDone
                            ? `${ui('foodCount', { count: item.foods })} · ${ui('mealPlan', { kcal: fmt(item.calories) })}`
                            : isNext
                              ? ui('next')
                              : ui('notYet')}
                        </span>
                      </span>
                      <span className={`lp-cal__tl-kcal${isDone ? '' : ' is-muted'}`}>{ui('kcal', { kcal: fmt(item.calories) })}</span>
                      {index < PLATE_MEALS.length - 1 && <i className="lp-cal__line" />}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          <div className="lp-cal__side">
            <div className="lp-cal__next">
              <div className="lp-cal__next-head">
                <span className="lp-cal__disc" aria-hidden="true">
                  <Apple />
                </span>
                <span>
                  <span className="lp-cal__next-title">
                    {meal} {!logged && <Pill>{ui('next')}</Pill>}
                  </span>
                  <span className="lp-cal__next-meta">{ui('plannedMeta', { kcal: fmt(snackKcal), count: SNACK.foods.length })}</span>
                </span>
              </div>
              <ul className="lp-cal__foods">
                {SNACK.foods.map((food) => (
                  <li key={food.en}>
                    <span>
                      {name(food)} <em>{`${food.grams} g`}</em>
                    </span>
                    <b>{ui('kcal', { kcal: fmt(food.calories) })}</b>
                  </li>
                ))}
              </ul>
              {logged ? (
                <div className="lp-cal__done">
                  <p>
                    <Check aria-hidden="true" />
                    {ui('toastPlanned', { meal })}
                  </p>
                  <button type="button" ref={undoRef} className="lp-btn lp-btn--quiet lp-btn--sm" onClick={() => toggle(false)}>
                    {ui('undo')}
                  </button>
                </div>
              ) : (
                <div className="lp-cal__acts">
                  <button type="button" ref={logRef} className="lp-btn lp-btn--go lp-cal__log" onClick={() => toggle(true)}>
                    <Check aria-hidden="true" />
                    {ui('logPlanned', { kcal: fmt(snackKcal) })}
                  </button>
                  <span className="lp-btn lp-btn--quiet lp-cal__else" aria-hidden="true">
                    {ui('ateSomethingElse')}
                  </span>
                </div>
              )}
              <p className="sr-only" aria-live="polite">
                {announcement}
              </p>
            </div>

            <div className="lp-cal__sheet" aria-hidden="true">
              <p className="lp-cal__sheet-title">{meal}</p>
              <p className="lp-cal__sheet-sub">{ui('sheetSub', { day: ui('todayLower') })}</p>
              <span className="lp-seg">
                <span>
                  {ui('tabPlan')} <i>2</i>
                </span>
                <span className="is-on">
                  {ui('tabSearch')} <i>1</i>
                </span>
              </span>
              <p className="lp-cal__sel-title">{ui('selectionTitle')}</p>
              <ul className="lp-cal__sel">
                {picks.map((pick) => (
                  <li key={pick.en}>
                    <span>
                      <b>{name(pick)}</b>
                      <span>{`${pick.grams} g · ${pick.source}`}</span>
                    </span>
                    <b>{ui('kcal', { kcal: fmt(pick.calories) })}</b>
                  </li>
                ))}
              </ul>
              <span className="lp-btn lp-btn--go lp-cal__add">{ui('addSelection', { count: picks.length, kcal: fmt(sum(picks, 'calories')) })}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
